import { apiClient, type UploadOptions } from './apiClient';
import { isPersonalizedFeedEnabledForUser, getSessionId } from '../utils/featureFlags';

/**
 * Archive Service
 * Handles Archive CRUD operations and actions for Instagram-style posts with carousel support
 */

export interface ArchiveImage {
  id: string;
  postId: string;
  imageUrl: string;
  storagePath?: string;
  bucket: string;
  position: number;
  createdAt: string;
}

export interface ArchiveUser {
  id: string;
  fullName: string;
  username?: string;
  profilePicture: string | null;
  isVerified: boolean;
}

/** How long a creator usually needs. Mirrors the backend's READY_IN_OPTIONS. */
export type ReadyIn = '1 to 2 weeks' | '2 to 4 weeks' | '4 to 6 weeks' | '6 weeks +';

export interface ArchivePost {
  id: string;
  userId: string;
  user: ArchiveUser;
  caption?: string;
  tags: string[];
  /** Occasions this piece suits. Empty when the creator skipped the step. */
  occasion: string[];
  /** Null when unset — every discovery field is optional. */
  readyIn: ReadyIn | null;
  status: 'draft' | 'published';
  likeCount: number;
  commentCount: number;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  sourcePortfolioItemId?: string;
  images: ArchiveImage[];
}

export interface ArchiveStats {
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalViews: number;
  totalLikes: number;
  averageViewsPerPost: number;
  averageLikesPerPost: number;
}

export interface PaginatedArchiveResponse {
  items: ArchivePost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateArchiveData {
  caption?: string;
  tags?: string[];
  occasion?: string[];
  readyIn?: ReadyIn | null;
  status?: 'draft' | 'published';
  mentionedUsers?: string[];
  /**
   * Stable across retries of the same post. If a reply was lost on a flaky
   * connection after the server already saved the post, resending the same
   * key returns that post instead of creating a duplicate.
   */
  idempotencyKey?: string;
}

export interface UpdateArchiveData {
  caption?: string;
  tags?: string[];
  occasion?: string[];
  readyIn?: ReadyIn | null;
  status?: 'draft' | 'published';
  mentionedUsers?: string[];
}

export interface ArchiveFeedParams {
  page?: number;
  limit?: number;
  userId?: string;
  status?: 'draft' | 'published';
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface PersonalizedFeedOptions {
  sessionId?: string;
  filters?: Omit<ArchiveFeedParams, 'sortBy'>;
  forceDefault?: boolean;
}

export interface PersonalizedFeedResponse extends PaginatedArchiveResponse {
  meta?: {
    mode?: 'personalized' | 'default';
    processingTimeMs?: number;
    algorithm?: string;
  };
}

export const archiveService = {
  /**
   * Create archive post with images (1-10 images)
   * POST /archives (multipart/form-data)
   */
  createPost: async (
    images: File[],
    data?: CreateArchiveData,
    options?: Pick<UploadOptions, 'onUploadProgress' | 'signal'>,
  ) => {
    const formData = new FormData();
    
    // Add images (field name must be 'images' as per API docs)
    images.forEach(image => {
      formData.append('images', image);
    });
    
    // Add metadata. Every discovery field is optional — omitted entirely when
    // the creator skipped the "Help clients find it" step.
    if (data?.caption) formData.append('caption', data.caption);
    if (data?.tags && data.tags.length > 0) {
      formData.append('tags', JSON.stringify(data.tags));
    }
    if (data?.occasion && data.occasion.length > 0) {
      formData.append('occasion', JSON.stringify(data.occasion));
    }
    if (data?.readyIn) formData.append('readyIn', data.readyIn);
    // The API field is `mentionedUserIds`; this was previously never sent, so
    // mentions were silently dropped on create.
    if (data?.mentionedUsers && data.mentionedUsers.length > 0) {
      formData.append('mentionedUserIds', JSON.stringify(data.mentionedUsers));
    }
    // Default to published status
    formData.append('status', data?.status || 'published');
    if (data?.idempotencyKey) formData.append('idempotencyKey', data.idempotencyKey);

    // timeout 0: the caller runs a stall watchdog instead. A fixed deadline
    // punishes a slow-but-progressing upload, which is the common case here.
    return await apiClient.uploadFile<{ post: ArchivePost }>('/archives', formData, 'POST', {
      ...options,
      timeout: 0,
    });
  },

  /**
   * Get public archive feed (published posts only)
   * GET /archives
   */
  getFeed: async (params?: ArchiveFeedParams) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);

    const url = `/archives${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await apiClient.get<PaginatedArchiveResponse>(url);
    
    return response;
  },

  /**
   * Get authenticated user's archive posts (drafts + published)
   * GET /archives/me
   */
  getMine: async (params?: Omit<ArchiveFeedParams, 'userId'>) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.order) queryParams.append('order', params.order);

    const url = `/archives/me${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await apiClient.get<PaginatedArchiveResponse>(url);
  },

  /**
   * Get authenticated user's archive statistics
   * GET /archives/me/stats
   */
  getMyStats: async () => {
    return await apiClient.get<ArchiveStats>('/archives/me/stats');
  },

  /**
   * Get single archive post by ID
   * GET /archives/:id
   */
  getById: async (id: string) => {
    return await apiClient.get<{ post: ArchivePost }>(`/archives/${id}`);
  },

  /**
   * Update archive post (caption, discovery fields, status)
   * PATCH /archives/:id
   */
  update: async (id: string, data: UpdateArchiveData) => {
    const { mentionedUsers, ...rest } = data;
    const body: Record<string, unknown> = { ...rest };
    // The API field is `mentionedUserIds`.
    if (mentionedUsers !== undefined) body.mentionedUserIds = mentionedUsers;
    return await apiClient.patch<{ post: ArchivePost }>(`/archives/${id}`, body);
  },

  /**
   * Delete archive post and images
   * DELETE /archives/:id
   */
  remove: async (id: string) => {
    return await apiClient.delete(`/archives/${id}`);
  },

  /**
   * Like an archive post (public endpoint)
   * POST /archives/:id/like
   */
  like: async (id: string) => {
    return await apiClient.post(`/archives/${id}/like`);
  },

  /**
   * Update archive post status
   * PATCH /archives/:id
   */
  updateStatus: async (id: string, status: 'draft' | 'published') => {
    return await apiClient.patch<{ post: ArchivePost }>(`/archives/${id}`, { status });
  },

  /**
   * Get personalized archive feed using AI ranking algorithm
   * GET /archives?sortBy=personalized
   */
  getPersonalizedFeed: async (page: number = 1, limit: number = 20, options: PersonalizedFeedOptions = {}) => {
    const params = new URLSearchParams({
      sortBy: 'personalized',
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add any additional filters
    if (options.filters?.userId) params.append('userId', options.filters.userId);
    if (options.filters?.status) params.append('status', options.filters.status);
    if (options.filters?.order) params.append('order', options.filters.order);

    // Prepare headers for session tracking
    const headers: Record<string, string> = {};
    if (options.sessionId) {
      headers['x-session-id'] = options.sessionId;
    }

    try {
      const response = await apiClient.get<PersonalizedFeedResponse>(
        `/archives?${params.toString()}`,
        { headers }
      );

      // Track successful personalized feed load
      if (typeof window !== 'undefined' && (window as any).analytics && response.success) {
        (window as any).analytics.track('Personalized Feed Loaded', {
          page,
          limit,
          itemsCount: response.data.items?.length || 0,
          processingTime: response.data.meta?.processingTimeMs,
          algorithm: response.data.meta?.mode,
        });
      }

      return response;
    } catch (error) {
      // console.warn('Personalized feed failed, falling back to default:', error);
      
      // Automatic fallback to default feed
      return archiveService.getFeed({
        page,
        limit,
        ...options.filters,
      });
    }
  },

  /**
   * Smart feed method - automatically chooses the best algorithm
   * Uses personalized feed if enabled, otherwise falls back to default
   */
  getSmartFeed: async (page: number = 1, limit: number = 20, options: PersonalizedFeedOptions = {}) => {
    // Check if personalized feed should be used
    if (isPersonalizedFeedEnabledForUser() && !options.forceDefault) {
      return archiveService.getPersonalizedFeed(page, limit, {
        ...options,
        sessionId: options.sessionId || getSessionId(),
      });
    }

    // Fall back to default feed
    return archiveService.getFeed({
      page,
      limit,
      ...options.filters,
    });
  },
};
